(function () {
  if (window.AxiomShopByCategoryInit) return;
  window.AxiomShopByCategoryInit = true;

  const PANEL_DURATION = 420;
  const MOBILE_BREAKPOINT = 989;
  const RAIL_SPEED = 700;

  function ensureSwiper(root) {
    if (!root) return Promise.resolve();
    if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);
    if (window.Axiom && typeof window.Axiom.loadSwiperAssets === 'function') {
      return window.Axiom.loadSwiperAssets(root.dataset.swiperCss, root.dataset.swiperScript);
    }
    return Promise.resolve();
  }

  function shouldUseSwiper(root) {
    return getLayoutMode(root) === 'slider' && window.innerWidth > MOBILE_BREAKPOINT;
  }

  function getTabs(root) {
    return Array.from(root.querySelectorAll('[data-shop-by-category-tab]'));
  }

  function getLayoutMode(root) {
    return root?.dataset.layoutMode || 'slider';
  }

  const TYPE_STYLE_CLASSES = [
    'type-style--display',
    'type-style--page-title',
    'type-style--section-heading',
    'type-style--secondary-heading',
    'type-style--subheading',
    'type-style--body',
    'type-style--caption',
  ];

  function syncTextTabTypography(root) {
    if (!root?.dataset.textTabsActiveStyle || !root?.dataset.textTabsInactiveStyle) return;

    const activeMobileScale = Number(root.dataset.textTabsActiveMobileScale || 100);
    const shouldScaleTabHeadings = window.innerWidth <= 767 && activeMobileScale !== 100;

    getTabs(root).forEach((tab) => {
      const label = tab.querySelector('[data-shop-by-category-tab-label]');
      if (!label) return;

      label.style.removeProperty('font-size');
      label.style.removeProperty('transform');
      const requestedStyle = tab.getAttribute('aria-selected') === 'true'
        ? root.dataset.textTabsActiveStyle
        : root.dataset.textTabsInactiveStyle;
      const categoryStyle = label.dataset.shopByCategoryHeadingStyle || 'caption';
      const resolvedStyle = requestedStyle === 'category' ? categoryStyle : requestedStyle;

      label.classList.remove(...TYPE_STYLE_CLASSES);
      label.classList.add('type-style--' + resolvedStyle.replace(/_/g, '-'));

      if (shouldScaleTabHeadings) {
        const baseFontSize = Number.parseFloat(window.getComputedStyle(label).fontSize);
        if (Number.isFinite(baseFontSize)) {
          label.style.fontSize = (baseFontSize * (activeMobileScale / 100)).toFixed(2) + 'px';
        }
      }
    });
  }

  function updatePillIndicator(root) {
    if (!root || !root.classList.contains('shop-by-category--tab-style-pill')) return;

    const tabsWrap = root.querySelector('.shop-by-category__tabs');
    const indicator = root.querySelector('[data-shop-by-category-tab-indicator]');
    const activeTab = root.querySelector('[data-shop-by-category-tab][aria-selected="true"]');

    if (!tabsWrap || !indicator || !activeTab) {
      if (indicator) indicator.classList.remove('is-visible');
      return;
    }

    const nextRect = {
      x: activeTab.offsetLeft,
      y: activeTab.offsetTop,
      width: activeTab.offsetWidth,
      height: activeTab.offsetHeight,
    };
    let previousRect = indicator._shopByCategoryPillRect;
    const finalTransform = `translate3d(${nextRect.x.toFixed(2)}px, ${nextRect.y.toFixed(2)}px, 0)`;

    if (indicator._shopByCategoryPillAnimation) {
      const tabsRect = tabsWrap.getBoundingClientRect();
      const indicatorRect = indicator.getBoundingClientRect();
      previousRect = {
        x: indicatorRect.left - tabsRect.left + tabsWrap.scrollLeft,
        y: indicatorRect.top - tabsRect.top + tabsWrap.scrollTop,
        width: indicatorRect.width,
        height: indicatorRect.height,
      };
      indicator._shopByCategoryPillAnimation.cancel();
      indicator._shopByCategoryPillAnimation = null;
    }

    indicator.style.width = nextRect.width.toFixed(2) + 'px';
    indicator.style.height = nextRect.height.toFixed(2) + 'px';
    indicator.style.transform = finalTransform;
    indicator.classList.add('is-visible');

    const geometryChanged = previousRect
      && (previousRect.x !== nextRect.x
        || previousRect.y !== nextRect.y
        || previousRect.width !== nextRect.width
        || previousRect.height !== nextRect.height);

    if (geometryChanged && !prefersReducedMotion() && typeof indicator.animate === 'function') {
      const scaleX = previousRect.width / Math.max(nextRect.width, 1);
      const scaleY = previousRect.height / Math.max(nextRect.height, 1);
      const startTransform = `translate3d(${previousRect.x.toFixed(2)}px, ${previousRect.y.toFixed(2)}px, 0) scale(${scaleX.toFixed(4)}, ${scaleY.toFixed(4)})`;
      const animation = indicator.animate(
        [
          { transform: startTransform },
          { transform: finalTransform },
        ],
        {
          duration: PANEL_DURATION,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        }
      );

      indicator._shopByCategoryPillAnimation = animation;
      animation.addEventListener('finish', function () {
        if (indicator._shopByCategoryPillAnimation === animation) {
          indicator._shopByCategoryPillAnimation = null;
        }
      }, { once: true });
    }

    indicator._shopByCategoryPillRect = nextRect;
  }

  function getActiveRail(root) {
    const activePanel = root && root.querySelector('[data-shop-by-category-panel].is-active');
    return activePanel ? activePanel.querySelector('[data-shop-by-category-rail]') : null;
  }

  function updateRailIndicator(root, rail) {
    if (!root) return;

    const indicator = root.querySelector('[data-shop-by-category-scroll-indicator]');
    const indicatorValue = root.querySelector('[data-shop-by-category-scroll-indicator-value]');
    const activeRail = getActiveRail(root);
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const hasOverflow = activeRail && activeRail.scrollWidth > activeRail.clientWidth + 1;
    const showIndicator = getLayoutMode(root) === 'slider' && isMobile && hasOverflow;

    if (!indicator) return;

    indicator.hidden = !showIndicator;
    if (!showIndicator || !indicatorValue || (rail && rail !== activeRail)) return;

    const items = Array.from(activeRail.querySelectorAll('[data-shop-by-category-item]'));
    if (items.length === 0) return;

    const railRect = activeRail.getBoundingClientRect();
    const firstRect = items[0].getBoundingClientRect();
    const isRtl = window.getComputedStyle(activeRail).direction === 'rtl';
    const scrollableWidth = Math.max(activeRail.scrollWidth - activeRail.clientWidth, 0);
    const scrollOffset = isRtl
      ? Math.max(firstRect.right - railRect.right, 0)
      : Math.max(railRect.left - firstRect.left, 0);
    const visibleProgress = activeRail.scrollWidth > 0 ? activeRail.clientWidth / activeRail.scrollWidth : 1;
    const scrollProgress = scrollableWidth > 0 ? Math.min(scrollOffset / scrollableWidth, 1) : 1;
    const progress = Math.min(visibleProgress + scrollProgress * (1 - visibleProgress), 1);

    indicatorValue.style.setProperty('--horizontal-scroll-progress', progress.toFixed(4));
  }

  function getRailSwiper(rail) {
    if (!rail || !rail._shopByCategorySwiper || rail._shopByCategorySwiper.destroyed) return null;
    return rail._shopByCategorySwiper;
  }

  function getTabsSwiper(root) {
    if (!root || !root._shopByCategoryTabsSwiper || root._shopByCategoryTabsSwiper.destroyed) return null;
    return root._shopByCategoryTabsSwiper;
  }

  function getVisibleProductCount(rail) {
    if (!rail) return 0;
    return rail.querySelectorAll('[data-shop-by-category-item]').length;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getCssNumber(root, propertyName, fallback) {
    const value = parseFloat(window.getComputedStyle(root).getPropertyValue(propertyName));
    return Number.isFinite(value) ? value : fallback;
  }

  function getRailGap(root) {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      return getCssNumber(root, '--shop-by-category-gap-mobile', 12);
    }

    return getCssNumber(root, '--shop-by-category-gap-desktop', 24);
  }

  function getSlidesVisibleTarget(root) {
    const desktopColumns = getCssNumber(root, '--shop-by-category-columns', 4);
    const mobileColumns = getCssNumber(root, '--shop-by-category-columns-mobile', 2);

    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      return Math.max(1.2, mobileColumns - 0.35);
    }

    if (window.innerWidth < 1024) {
      return Math.max(1.5, Math.min(desktopColumns, 4) - 0.5);
    }

    return Math.max(1.5, desktopColumns - 0.5);
  }

  function updateRailSlideMetrics(root, rail) {
    if (!root || !rail) return;

    const container = rail.closest('.layout-container');
    const containerWidth = container ? container.clientWidth : root.clientWidth;
    const styles = window.getComputedStyle(container || root);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const contentWidth = Math.max(containerWidth - paddingLeft - paddingRight, 0);
    const visibleTarget = getSlidesVisibleTarget(root);
    const gap = getRailGap(root);
    const slideWidth = Math.max((contentWidth - ((visibleTarget - 1) * gap)) / visibleTarget, 120);

    rail.style.setProperty('--shop-by-category-rail-gap', gap.toFixed(2) + 'px');
    rail.style.setProperty('--shop-by-category-slide-width', slideWidth.toFixed(2) + 'px');
  }

  function getCurrentSlidesPerView(swiper) {
    if (!swiper || !swiper.params) return 1;
    if (swiper.params.slidesPerView === 'auto' && typeof swiper.slidesPerViewDynamic === 'function') {
      return swiper.slidesPerViewDynamic();
    }
    const slidesPerView = parseFloat(swiper.params.slidesPerView);
    return Number.isFinite(slidesPerView) ? slidesPerView : 1;
  }

  function updateRailButtons(root, rail) {
    if (!root) return;

    updateRailIndicator(root, rail);
    if (getLayoutMode(root) !== 'slider') return;

    const currentActiveRail = getActiveRail(root);
    if (rail && currentActiveRail && rail !== currentActiveRail) return;

    const activeRail = currentActiveRail || rail;
    const prevButton = root.querySelector('[data-shop-by-category-scroll-prev]');
    const nextButton = root.querySelector('[data-shop-by-category-scroll-next]');
    if (!prevButton || !nextButton) return;

    if (!activeRail) {
      prevButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    const swiper = getRailSwiper(activeRail);
    if (swiper) {
      const visibleCount = getVisibleProductCount(activeRail);
      const isLocked = swiper.isLocked || visibleCount <= getCurrentSlidesPerView(swiper) + 0.01;
      prevButton.disabled = isLocked || swiper.isBeginning;
      nextButton.disabled = isLocked || swiper.isEnd;
      return;
    }

    const maxScroll = Math.max(activeRail.scrollWidth - activeRail.clientWidth, 0);
    const atStart = activeRail.scrollLeft <= 4;
    const atEnd = activeRail.scrollLeft >= maxScroll - 4 || maxScroll <= 0;
    prevButton.disabled = atStart;
    nextButton.disabled = atEnd;
  }

  function updateActiveRailButtons(root) {
    updateRailButtons(root, getActiveRail(root));
  }

  function scrollTabIntoRail(root, activeTab) {
    if (!root || !activeTab || window.innerWidth > MOBILE_BREAKPOINT) return;

    const tabsRail = root.querySelector('[data-shop-by-category-tabs-rail]');
    if (!tabsRail) return;

    const railRect = tabsRail.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const edgeInset = 8;
    let targetLeft = tabsRail.scrollLeft;

    if (tabRect.left < railRect.left + edgeInset) {
      targetLeft -= (railRect.left + edgeInset) - tabRect.left;
    } else if (tabRect.right > railRect.right - edgeInset) {
      targetLeft += tabRect.right - (railRect.right - edgeInset);
    } else {
      return;
    }

    const maxScrollLeft = Math.max(tabsRail.scrollWidth - tabsRail.clientWidth, 0);
    tabsRail.scrollTo({
      left: Math.min(Math.max(0, targetLeft), maxScrollLeft),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  function scrollActiveRail(root, direction) {
    const rail = getActiveRail(root);
    if (!rail) return;

    const swiper = getRailSwiper(rail);
    if (swiper) {
      if (direction < 0) {
        swiper.slidePrev();
      } else {
        swiper.slideNext();
      }
      updateRailButtons(root, rail);
      return;
    }

    rail.scrollBy({
      left: Math.max(rail.clientWidth * 0.78, 240) * direction,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  function bindRails(root) {
    if (getLayoutMode(root) !== 'slider') return;

    root.querySelectorAll('[data-shop-by-category-rail]').forEach((rail) => {
      const shouldUseNativeMobileRail = window.innerWidth <= MOBILE_BREAKPOINT;

      if (rail.dataset.shopByCategoryRailBound === 'true') {
        updateRailSlideMetrics(root, rail);
        const existingSwiper = getRailSwiper(rail);
        if (shouldUseNativeMobileRail && existingSwiper) {
          existingSwiper.destroy(true, true);
          rail._shopByCategorySwiper = null;
          rail.dataset.shopByCategoryRailMode = 'native';
        } else if (existingSwiper) {
          existingSwiper.params.spaceBetween = getRailGap(root);
          existingSwiper.update();
        }
        updateRailButtons(root, rail);
        return;
      }

      rail.dataset.shopByCategoryRailBound = 'true';
      updateRailSlideMetrics(root, rail);
      rail.dataset.shopByCategoryRailMode = shouldUseNativeMobileRail ? 'native' : 'swiper';

      if (!shouldUseNativeMobileRail && typeof window.Swiper === 'function') {
        const swiper = new window.Swiper(rail, {
          slidesPerView: 'auto',
          spaceBetween: getRailGap(root),
          speed: prefersReducedMotion() ? 0 : RAIL_SPEED,
          slidesPerGroup: 1,
          roundLengths: false,
          watchOverflow: true,
          resistanceRatio: 0.72,
          grabCursor: true,
          on: {
            init: function () {
              updateRailButtons(root, rail);
            },
            slideChange: function () {
              updateRailButtons(root, rail);
            },
            reachBeginning: function () {
              updateRailButtons(root, rail);
            },
            reachEnd: function () {
              updateRailButtons(root, rail);
            },
            fromEdge: function () {
              updateRailButtons(root, rail);
            },
            resize: function () {
              updateRailSlideMetrics(root, rail);
              this.params.spaceBetween = getRailGap(root);
              updateRailButtons(root, rail);
            },
          },
        });
        rail._shopByCategorySwiper = swiper;
      } else {
        const onScroll = function () {
          if (rail === getActiveRail(root)) updateRailButtons(root, rail);
        };
        rail.addEventListener('scroll', onScroll, { passive: true });
        rail._shopByCategoryFallbackCleanup = function () {
          rail.removeEventListener('scroll', onScroll);
        };
      }

      root._shopByCategoryRailCleanups.push(function () {
        const swiper = getRailSwiper(rail);
        if (swiper) {
          swiper.destroy(true, true);
          rail._shopByCategorySwiper = null;
        }
        if (typeof rail._shopByCategoryFallbackCleanup === 'function') {
          rail._shopByCategoryFallbackCleanup();
          rail._shopByCategoryFallbackCleanup = null;
        }
        rail.dataset.shopByCategoryRailBound = 'false';
      });
    });
  }

  function bindTabsRail(root) {
    const tabsRail = root.querySelector('[data-shop-by-category-tabs-rail]');
    if (!tabsRail) return;

    const existingSwiper = getTabsSwiper(root);
    if (existingSwiper) {
      existingSwiper.destroy(true, true);
      root._shopByCategoryTabsSwiper = null;
    }
  }

  function resetRail(rail) {
    if (!rail) return;
    const root = rail.closest('[data-shop-by-category]');
    updateRailSlideMetrics(root, rail);
    const swiper = getRailSwiper(rail);
    if (swiper) {
      swiper.params.spaceBetween = getRailGap(root);
      swiper.update();
      swiper.slideTo(0, 0);
      return;
    }
    rail.scrollLeft = 0;
  }

  function activateTab(root, targetId, moveFocus) {
    if (!root || !targetId) return;

    const tabs = root.querySelectorAll('[data-shop-by-category-tab]');
    const panels = root.querySelectorAll('[data-shop-by-category-panel]');
    const currentPanel = root.querySelector('[data-shop-by-category-panel].is-active');
    const nextPanel = root.querySelector('#' + CSS.escape(targetId));
    let activeTab = null;

    if (!nextPanel) return;

    tabs.forEach((tab) => {
      const isActive = tab.getAttribute('aria-controls') === targetId;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive) activeTab = tab;
    });

    syncTextTabTypography(root);

    const viewAllLink = root.querySelector('[data-shop-by-category-view-all]');
    if (viewAllLink && activeTab) {
      viewAllLink.setAttribute('href', activeTab.dataset.collectionUrl || '/collections');
    }

    updatePillIndicator(root);
    const tabsSwiper = getTabsSwiper(root);
    if (tabsSwiper) tabsSwiper.update();
    if (activeTab && window.innerWidth <= MOBILE_BREAKPOINT) {
      scrollTabIntoRail(root, activeTab);
    }

    if (currentPanel === nextPanel) {
      panels.forEach((panel) => {
        const isTarget = panel === nextPanel;
        panel.hidden = !isTarget;
        panel.classList.toggle('is-active', isTarget);
        panel.classList.remove('is-switching');
      });
      resetRail(nextPanel.querySelector('[data-shop-by-category-rail]'));
      updateActiveRailButtons(root);
      if (moveFocus && activeTab) activeTab.focus();
      return;
    }

    if (root._shopByCategoryTimeout) {
      clearTimeout(root._shopByCategoryTimeout);
      root._shopByCategoryTimeout = null;
    }

    panels.forEach((panel) => {
      const isTarget = panel === nextPanel;
      panel.hidden = !isTarget;
      panel.classList.toggle('is-active', isTarget);
      panel.classList.remove('is-switching');
    });

    resetRail(nextPanel.querySelector('[data-shop-by-category-rail]'));

    nextPanel.classList.add('is-switching');
    if (window.Axiom && typeof window.Axiom.revealElements === 'function') {
      window.Axiom.revealElements(nextPanel, {
        trigger: 'manual',
        reset: true,
        selector: '[data-reveal-item]',
        stagger: 58,
      });
    }
    root._shopByCategoryTimeout = setTimeout(function () {
      nextPanel.classList.remove('is-switching');
      root._shopByCategoryTimeout = null;
    }, PANEL_DURATION);

    updateActiveRailButtons(root);
    const tabList = getTabs(root);
    const activeIndex = tabList.indexOf(activeTab);
    if (tabsSwiper && activeIndex > -1) {
      tabsSwiper.slideTo(activeIndex);
    }
    if (moveFocus && activeTab) activeTab.focus();
  }

  function setupRoot(root) {
    if (!root || root.dataset.shopByCategoryBound === 'true') return;
    root.dataset.shopByCategoryBound = 'true';
    root._shopByCategoryRailCleanups = [];
    root._shopByCategoryLastLayoutWidth = window.innerWidth;
    root._shopByCategoryLastMobileState = window.innerWidth <= MOBILE_BREAKPOINT;

    const selectedTab = root.querySelector('[data-shop-by-category-tab][aria-selected="true"]');
    if (selectedTab) activateTab(root, selectedTab.getAttribute('aria-controls'), false);
    bindRails(root);
    bindTabsRail(root);
    updatePillIndicator(root);
    updateActiveRailButtons(root);

    const rebuildResponsiveRails = function () {
      const nextWidth = window.innerWidth;
      const nextMobileState = nextWidth <= MOBILE_BREAKPOINT;
      root._shopByCategoryLastLayoutWidth = nextWidth;
      root._shopByCategoryLastMobileState = nextMobileState;

      bindTabsRail(root);
      syncTextTabTypography(root);
      if (root._shopByCategoryRailCleanups) {
        root._shopByCategoryRailCleanups.forEach((cleanup) => cleanup());
        root._shopByCategoryRailCleanups = [];
      }
      root.querySelectorAll('[data-shop-by-category-rail]').forEach((rail) => {
        rail.dataset.shopByCategoryRailBound = 'false';
      });
      bindRails(root);
      root.querySelectorAll('[data-shop-by-category-rail]').forEach((rail) => {
        updateRailSlideMetrics(root, rail);
        const swiper = getRailSwiper(rail);
        if (swiper) {
          swiper.params.spaceBetween = getRailGap(root);
          swiper.update();
        }
      });
      updatePillIndicator(root);
      updateActiveRailButtons(root);
    };

    const handleResize = function () {
      const nextWidth = window.innerWidth;
      const nextMobileState = nextWidth <= MOBILE_BREAKPOINT;
      const previousWidth = root._shopByCategoryLastLayoutWidth || nextWidth;
      const previousMobileState = root._shopByCategoryLastMobileState;
      const widthChanged = Math.abs(nextWidth - previousWidth) > 2;
      const breakpointChanged = previousMobileState !== nextMobileState;

      if (!widthChanged && !breakpointChanged) return;

      if (root._shopByCategoryResizeTimer) {
        window.clearTimeout(root._shopByCategoryResizeTimer);
      }

      root._shopByCategoryResizeTimer = window.setTimeout(function () {
        root._shopByCategoryResizeTimer = null;

        if (shouldUseSwiper(root) && typeof window.Swiper !== 'function') {
          ensureSwiper(root)
            .catch(function (error) {
              console.error(error);
            })
            .finally(rebuildResponsiveRails);
          return;
        }

        rebuildResponsiveRails();
      }, 160);
    };
    root._shopByCategoryResizeHandler = handleResize;
    window.addEventListener('resize', handleResize);

    root.addEventListener('click', function (event) {
      const prevButton = event.target.closest('[data-shop-by-category-scroll-prev]');
      const nextButton = event.target.closest('[data-shop-by-category-scroll-next]');
      if (prevButton && root.contains(prevButton)) {
        scrollActiveRail(root, -1);
        return;
      }
      if (nextButton && root.contains(nextButton)) {
        scrollActiveRail(root, 1);
        return;
      }

      const tab = event.target.closest('[data-shop-by-category-tab]');
      if (!tab || !root.contains(tab)) return;
      activateTab(root, tab.getAttribute('aria-controls'), false);
    });

    root.addEventListener('keydown', function (event) {
      const currentTab = event.target.closest('[data-shop-by-category-tab]');
      if (!currentTab || !root.contains(currentTab)) return;

      const tabs = getTabs(root);
      const currentIndex = tabs.indexOf(currentTab);
      if (currentIndex === -1) return;

      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      const nextTab = tabs[nextIndex];
      if (nextTab) activateTab(root, nextTab.getAttribute('aria-controls'), true);
    });

    root.__shopByCategoryCleanup = function () {
      if (root._shopByCategoryResizeHandler) {
        window.removeEventListener('resize', root._shopByCategoryResizeHandler);
        root._shopByCategoryResizeHandler = null;
      }
      if (root._shopByCategoryResizeTimer) {
        clearTimeout(root._shopByCategoryResizeTimer);
        root._shopByCategoryResizeTimer = null;
      }
      if (root._shopByCategoryTimeout) {
        clearTimeout(root._shopByCategoryTimeout);
        root._shopByCategoryTimeout = null;
      }
      const pillIndicator = root.querySelector('[data-shop-by-category-tab-indicator]');
      if (pillIndicator?._shopByCategoryPillAnimation) {
        pillIndicator._shopByCategoryPillAnimation.cancel();
        pillIndicator._shopByCategoryPillAnimation = null;
      }
      if (root._shopByCategoryRailCleanups) {
        root._shopByCategoryRailCleanups.forEach((cleanup) => cleanup());
        root._shopByCategoryRailCleanups = [];
      }
      const tabsSwiper = getTabsSwiper(root);
      if (tabsSwiper) {
        tabsSwiper.destroy(true, true);
        root._shopByCategoryTabsSwiper = null;
      }
      root.dataset.shopByCategoryBound = 'false';
      root.__shopByCategoryCleanup = null;
    };
  }

  function initRoot(root) {
    if (!root || root.dataset.shopByCategoryBound === 'true' || root.dataset.shopByCategoryPending === 'true') return;
    root.dataset.shopByCategoryPending = 'true';

    const ready = shouldUseSwiper(root) ? ensureSwiper(root) : Promise.resolve();

    ready
      .catch(function (error) {
        console.error(error);
      })
      .finally(function () {
        root.dataset.shopByCategoryPending = 'false';
        setupRoot(root);
      });
  }

  function observeRoot(root) {
    if (!root || root.dataset.shopByCategoryObserved === 'true') return;
    initRoot(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-shop-by-category]').forEach(observeRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-shop-by-category]').forEach(function (root) {
      root.dataset.shopByCategoryObserved = 'false';
      if (typeof root.__shopByCategoryCleanup === 'function') {
        root.__shopByCategoryCleanup();
      }
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    const block = event.target.closest('[data-shop-by-category-block-id]');
    if (!block) return;

    const root = block.closest('[data-shop-by-category]');
    const blockId = block.dataset.shopByCategoryBlockId;
    if (!root || !blockId) return;

    const tab = root.querySelector('[data-shop-by-category-tab][data-shop-by-category-block-id="' + blockId + '"]');
    if (!tab) return;

    activateTab(root, tab.getAttribute('aria-controls'), false);
  });
})();
